/**
 * Utilitário para integração com YouTube Data API v3
 * 
 * Para usar:
 * 1. Obtenha uma API Key em: https://console.cloud.google.com/
 * 2. Ative a YouTube Data API v3
 * 3. Crie um arquivo .env.local na raiz do projeto com:
 *    VUE_APP_YOUTUBE_API_KEY=sua_chave_aqui
 * 4. Reinicie o servidor de desenvolvimento
 * 
 * Sistema de Cache:
 * - search.list (vídeos) é armazenado em localStorage por 10 minutos
 * - search.list (lives) é armazenado em localStorage por 10 minutos
 * - Reduz consumo da API drasticamente
 * - Limite de 1 chamada a cada 10 minutos mesmo com refresh da página
 * - Lives só são buscadas aos Domingos após 18:45h
 */

const API_KEY = process.env.VUE_APP_YOUTUBE_API_KEY || ''
const CHANNEL_ID = 'UCh-CyGfVjqS1QXbPEIzesiQ' // ID do canal @ma.poderosodeus
const BASE_URL = 'https://www.googleapis.com/youtube/v3'
const CACHE_DURATION = 6 * 60 * 60 * 1000 // 6 horas em milissegundos (não usado mais - legado)
const VIDEO_CACHE_DURATION = 10 * 60 * 1000 // 10 minutos em milissegundos (para vídeos)
const LIVE_CACHE_DURATION = 10 * 60 * 1000 // 10 minutos em milissegundos (para lives)

/**
 * Verifica se o cache ainda é válido
 * @param {number} timestamp - Timestamp da última atualização
 * @param {number} duration - Duração do cache em milissegundos (padrão: CACHE_DURATION)
 * @returns {boolean} true se o cache ainda é válido
 */
function isCacheValid(timestamp, duration = CACHE_DURATION) {
  if (!timestamp) return false
  const now = Date.now()
  const diff = now - timestamp
  return diff < duration
}

/**
 * Salva dados no localStorage com timestamp
 * @param {string} key - Chave do cache
 * @param {any} data - Dados a serem salvos
 */
function saveToCache(key, data) {
  try {
    const cacheData = {
      data: data,
      timestamp: Date.now(),
      lastUpdate: new Date().toISOString()
    }
    localStorage.setItem(key, JSON.stringify(cacheData))
    console.log(`💾 [YouTube Cache] Cache salvo:`, {
      chave: key,
      timestamp: cacheData.lastUpdate,
      quantidadeItens: Array.isArray(data) ? data.length : 'N/A',
      tamanhoBytes: new Blob([JSON.stringify(cacheData)]).size
    })
  } catch (error) {
    console.error('❌ [YouTube Cache] Erro ao salvar cache no localStorage:', error)
  }
}

/**
 * Recupera dados do localStorage se ainda forem válidos
 * @param {string} key - Chave do cache
 * @param {number} cacheDuration - Duração do cache em milissegundos (padrão: CACHE_DURATION)
 * @returns {any|null} Dados do cache ou null se inválido/inexistente
 */
function getFromCache(key, cacheDuration = CACHE_DURATION) {
  try {
    const cached = localStorage.getItem(key)
    if (!cached) {
      console.log(`📭 [YouTube Cache] Nenhum cache encontrado para: ${key}`)
      return null
    }
    
    const cacheData = JSON.parse(cached)
    if (isCacheValid(cacheData.timestamp, cacheDuration)) {
      const ageMinutes = Math.floor((Date.now() - cacheData.timestamp) / 1000 / 60)
      const ageHours = Math.floor(ageMinutes / 60)
      const remainingMinutes = ageMinutes % 60
      const remainingCacheMinutes = Math.floor((cacheDuration - (Date.now() - cacheData.timestamp)) / 1000 / 60)
      
      console.log(`📦 [YouTube Cache] Usando cache:`, {
        chave: key,
        idade: ageHours > 0 ? `${ageHours}h ${remainingMinutes}min` : `${ageMinutes}min`,
        salvoPelaUltimaVezEm: new Date(cacheData.timestamp).toLocaleString('pt-BR'),
        quantidadeItens: Array.isArray(cacheData.data) ? cacheData.data.length : 'N/A',
        expiraEm: remainingCacheMinutes > 60 ? `${Math.floor(remainingCacheMinutes / 60)}h ${remainingCacheMinutes % 60}min` : `${remainingCacheMinutes}min`
      })
      return cacheData.data
    } else {
      const expiredAgo = Math.floor((Date.now() - cacheData.timestamp - cacheDuration) / 1000 / 60)
      console.log(`⏰ [YouTube Cache] Cache expirado:`, {
        chave: key,
        expirouHa: `${expiredAgo}min`,
        removendo: true
      })
      localStorage.removeItem(key)
      return null
    }
  } catch (error) {
    console.error('❌ [YouTube Cache] Erro ao ler cache do localStorage:', error)
    return null
  }
}

/**
 * Busca os vídeos mais recentes do canal
 * @param {number} maxResults - Número máximo de vídeos a retornar (padrão: 20)
 * @returns {Promise<Array>} Array com os vídeos
 */
export async function fetchChannelVideos(maxResults = 20) {
  if (!API_KEY) {
    console.error('❌ [YouTube API] API Key não configurada. Configure VUE_APP_YOUTUBE_API_KEY no arquivo .env.local')
    return []
  }

  const CACHE_KEY = `youtube_videos_${CHANNEL_ID}_${maxResults}`
  
  // Log de onde foi chamado
  const caller = new Error().stack.split('\n')[2]?.trim() || 'desconhecido'
  console.log(`🔍 [YouTube API] fetchChannelVideos() chamado por:`, caller)
  console.log(`🔍 [YouTube API] Buscando vídeos (maxResults: ${maxResults})...`)

  try {
    // Tenta buscar do cache primeiro (cache de 10 minutos para vídeos)
    const cachedVideos = getFromCache(CACHE_KEY, VIDEO_CACHE_DURATION)
    if (cachedVideos) {
      console.log(`✅ [YouTube API] ${cachedVideos.length} vídeos retornados do cache (10min)`)
      return cachedVideos
    }

    console.log('🌐 [YouTube API] Buscando vídeos da API do YouTube (cache não disponível ou expirado)...')

    // Busca os IDs dos vídeos do canal
    const searchResponse = await fetch(
      `${BASE_URL}/search?key=${API_KEY}&channelId=${CHANNEL_ID}&part=snippet,id&order=date&maxResults=${maxResults}&type=video`
    )
    
    if (!searchResponse.ok) {
      throw new Error(`Erro na API: ${searchResponse.status}`)
    }

    const searchData = await searchResponse.json()
    
    if (!searchData.items || searchData.items.length === 0) {
      return []
    }

    // Extrai os IDs dos vídeos
    const videoIds = searchData.items.map(item => item.id.videoId).join(',')

    // Busca os detalhes completos dos vídeos (incluindo estatísticas e duração)
    const videosResponse = await fetch(
      `${BASE_URL}/videos?key=${API_KEY}&id=${videoIds}&part=snippet,contentDetails,statistics,liveStreamingDetails`
    )

    if (!videosResponse.ok) {
      throw new Error(`Erro ao buscar detalhes: ${videosResponse.status}`)
    }

    const videosData = await videosResponse.json()

    // Formata os dados para uso no componente
    const formattedVideos = videosData.items.map(video => ({
      id: video.id,
      title: video.snippet.title,
      description: video.snippet.description,
      thumbnail: {
        default: video.snippet.thumbnails.default?.url,
        medium: video.snippet.thumbnails.medium?.url,
        high: video.snippet.thumbnails.high?.url,
        maxres: video.snippet.thumbnails.maxres?.url || video.snippet.thumbnails.high?.url
      },
      publishedAt: video.snippet.publishedAt,
      channelTitle: video.snippet.channelTitle,
      duration: formatDuration(video.contentDetails.duration),
      viewCount: parseInt(video.statistics.viewCount || 0),
      likeCount: parseInt(video.statistics.likeCount || 0),
      commentCount: parseInt(video.statistics.commentCount || 0),
      isLive: video.snippet.liveBroadcastContent === 'live',
      isUpcoming: video.snippet.liveBroadcastContent === 'upcoming',
      url: `https://www.youtube.com/watch?v=${video.id}`
    }))

    // Salva no cache antes de retornar
    saveToCache(CACHE_KEY, formattedVideos)
    console.log(`✅ [YouTube API] ${formattedVideos.length} vídeos carregados e salvos em cache`)

    return formattedVideos
  } catch (error) {
    console.error('❌ [YouTube API] Erro ao buscar vídeos:', error)
    return []
  }
}

/**
 * Busca apenas lives em andamento
 * @returns {Promise<Array>} Array com as lives ativas
 */
export async function fetchLiveStreams() {
  if (!API_KEY) {
    console.error('❌ [YouTube API] API Key não configurada para buscar lives')
    return []
  }

  const CACHE_KEY = `youtube_lives_${CHANNEL_ID}`
  
  // Log de onde foi chamado
  const caller = new Error().stack.split('\n')[2]?.trim() || 'desconhecido'
  console.log('🔍 [YouTube API] fetchLiveStreams() chamado por:', caller)
  console.log('🔍 [YouTube API] Verificando lives ao vivo...')

  try {
    // Tenta buscar do cache primeiro (cache de 10 minutos para lives)
    const cachedLives = getFromCache(CACHE_KEY, LIVE_CACHE_DURATION)
    if (cachedLives) {
      console.log(`✅ [YouTube API] ${cachedLives.length} lives retornadas do cache (10min)`)
      return cachedLives
    }

    console.log('🔴 [YouTube API] Buscando lives da API (cache não disponível ou expirado)...')

    const response = await fetch(
      `${BASE_URL}/search?key=${API_KEY}&channelId=${CHANNEL_ID}&part=snippet,id&eventType=live&type=video&maxResults=5`
    )

    if (!response.ok) {
      throw new Error(`Erro na API: ${response.status}`)
    }

    const data = await response.json()

    if (!data.items || data.items.length === 0) {
      return []
    }

    const videoIds = data.items.map(item => item.id.videoId).join(',')

    const videosResponse = await fetch(
      `${BASE_URL}/videos?key=${API_KEY}&id=${videoIds}&part=snippet,liveStreamingDetails,statistics`
    )

    const videosData = await videosResponse.json()

    const formattedLives = videosData.items.map(video => ({
      id: video.id,
      title: video.snippet.title,
      description: video.snippet.description,
      thumbnail: {
        default: video.snippet.thumbnails.default?.url,
        medium: video.snippet.thumbnails.medium?.url,
        high: video.snippet.thumbnails.high?.url,
        maxres: video.snippet.thumbnails.maxres?.url || video.snippet.thumbnails.high?.url
      },
      publishedAt: video.snippet.publishedAt,
      isLive: true,
      url: `https://www.youtube.com/watch?v=${video.id}`,
      concurrentViewers: parseInt(video.liveStreamingDetails?.concurrentViewers || 0)
    }))

    // Salva no cache antes de retornar
    saveToCache(CACHE_KEY, formattedLives)
    console.log(`✅ [YouTube API] ${formattedLives.length} lives carregadas e salvas em cache`)

    return formattedLives
  } catch (error) {
    console.error('❌ [YouTube API] Erro ao buscar lives:', error)
    return []
  }
}

/**
 * Converte duração do formato ISO 8601 para formato legível
 * @param {string} duration - Duração no formato ISO 8601 (ex: PT1H30M45S)
 * @returns {string} Duração formatada (ex: "1:30:45")
 */
function formatDuration(duration) {
  const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/)
  
  if (!match) return '0:00'

  const hours = (match[1] || '').replace('H', '')
  const minutes = (match[2] || '').replace('M', '')
  const seconds = (match[3] || '').replace('S', '')

  const parts = []
  
  if (hours) parts.push(hours)
  parts.push((minutes || '0').padStart(hours ? 2 : 1, '0'))
  parts.push((seconds || '0').padStart(2, '0'))

  return parts.join(':')
}

/**
 * Formata número de visualizações para formato legível
 * @param {number} count - Número de visualizações
 * @returns {string} Formato legível (ex: "1.2M", "350K")
 */
export function formatViewCount(count) {
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1) + 'M'
  }
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K'
  }
  return count.toString()
}

/**
 * Formata data relativa (ex: "2 dias atrás")
 * @param {string} dateString - Data no formato ISO
 * @returns {string} Data formatada
 */
export function formatRelativeDate(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now - date) / 1000)

  if (diffInSeconds < 60) return 'agora'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min atrás`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h atrás`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} dias atrás`
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} semanas atrás`
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} meses atrás`
  return `${Math.floor(diffInSeconds / 31536000)} anos atrás`
}
